import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';

export interface GeoOption {
  /** The value to store when this option is selected (e.g., ISO2 code or name). */
  value: string;
  /** Display label. */
  label: string;
  /** Optional flag emoji (for countries). */
  emoji?: string;
  /** Optional secondary meta rendered muted after the label (e.g. a phone code). */
  hint?: string;
}

interface GeoSelectProps {
  /** Unique id for the trigger button. */
  id: string;
  /** Label text shown above the select. */
  label: string;
  /** Whether this field is required (shows * and blocks submit if empty). */
  required?: boolean;
  /** Current selected value (the `value` of a GeoOption). */
  value: string;
  /** Called when the user selects an option. */
  onChange: (value: string) => void;
  /** Available options to display. */
  options: GeoOption[];
  /** Placeholder when no value is selected. */
  placeholder?: string;
  /** Disable interaction (e.g., city before state is chosen). */
  disabled?: boolean;
  /** Show a loading spinner instead of the chevron. */
  loading?: boolean;
  /** Borderless trigger (filled background + soft focus ring only) — matches
   * borderless text inputs. Default keeps the outline for other forms. */
  borderless?: boolean;
  /** Denser layout for tight surfaces (Settings cards): smaller label,
   * shorter trigger matching compact h-10 text inputs. */
  compact?: boolean;
  /** Extra classes appended to the trigger button — lets a caller match a
   * specific input style (e.g. an underline-only field). Tailwind overrides
   * the atom's defaults. */
  triggerClassName?: string;
  /**
   * Custom trigger label formatter. When set, replaces the default
   * "emoji + label + hint" in the trigger button with the returned string.
   * Useful for compact displays (e.g. just "🇪🇸 +34" without the country name).
   */
  formatTrigger?: (option: GeoOption | null) => string;
}

interface MenuPosition {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
  placement: 'top' | 'bottom';
}

const VIEWPORT_PADDING = 12;
const MENU_MAX_HEIGHT = 340;

/**
 * GeoSelect — a searchable, portal-based dropdown for the cascading
 * Country → State → City selectors. Matches the dark-glass design system used
 * in SellerDropdown and MultiSelectPopover.
 *
 * Features: search filter, keyboard navigation (ArrowUp/Down, Enter, Esc,
 * Home/End), click-outside to close, flag emoji display, loading & disabled
 * states.
 */
export const GeoSelect: React.FC<GeoSelectProps> = ({
  id,
  label,
  required = false,
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  loading = false,
  borderless = false,
  compact = false,
  triggerClassName = '',
  formatTrigger,
}) => {

  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef(new Map<string, HTMLButtonElement>());

  const menuId = `${id}-menu`;
  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value) || null,
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    // Match by label (language/country name) or raw value (ISO / BCP 47 code).
    return options.filter(
      (opt) => opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q),
    );
  }, [options, search]);

  const updateMenuPosition = useCallback(() => {
    if (typeof window === 'undefined' || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const availableWidth = window.innerWidth - VIEWPORT_PADDING * 2;
    const width = Math.min(Math.max(rect.width, 280), availableWidth);
    const left = Math.min(
      Math.max(rect.left, VIEWPORT_PADDING),
      window.innerWidth - width - VIEWPORT_PADDING,
    );
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    const spaceAbove = rect.top - VIEWPORT_PADDING;
    const placement = spaceBelow < 260 && spaceAbove > spaceBelow ? 'top' : 'bottom';
    const availableHeight = placement === 'top' ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(190, Math.min(MENU_MAX_HEIGHT, availableHeight - 8));

    setMenuPosition({ left, top: placement === 'top' ? rect.top - 8 : rect.bottom + 8, width, maxHeight, placement });
  }, []);

  const closeMenu = useCallback((restoreFocus = false) => {
    setOpen(false);
    setSearch('');
    if (restoreFocus) {
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        closeMenu();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu(true);
      }
    };

    updateMenuPosition();
    const positionFrame = window.requestAnimationFrame(updateMenuPosition);
    const focusFrame = window.requestAnimationFrame(() => {
      searchRef.current?.focus();
    });

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.cancelAnimationFrame(positionFrame);
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [closeMenu, open, updateMenuPosition]);

  const selectOption = (nextValue: string) => {
    onChange(nextValue);
    closeMenu(true);
  };

  const moveOptionFocus = (currentIndex: number, direction: 1 | -1) => {
    const count = filteredOptions.length;
    if (count === 0) return;
    const nextIndex = (currentIndex + direction + count) % count;
    const nextValue = filteredOptions[nextIndex].value;
    optionRefs.current.get(nextValue)?.focus();
  };

  const handleOptionKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    optionIndex: string,
  ) => {
    const idx = parseInt(optionIndex, 10);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveOptionFocus(idx, 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveOptionFocus(idx, -1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectOption(filteredOptions[idx].value);
    } else if (event.key === 'Home') {
      event.preventDefault();
      filteredOptions[0] && optionRefs.current.get('0')?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      const last = filteredOptions.length - 1;
      last >= 0 && optionRefs.current.get(String(last))?.focus();
    } else if (event.key === 'Tab') {
      closeMenu();
    }
  };

  const assignOptionRef = (index: string) => (element: HTMLButtonElement | null) => {
    if (element) optionRefs.current.set(index, element);
    else optionRefs.current.delete(index);
  };

  const menu = open && typeof document !== 'undefined'
    ? createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="listbox"
          aria-label={label}
          style={menuPosition
            ? {
                left: menuPosition.left,
                top: menuPosition.top,
                width: menuPosition.width,
                maxHeight: menuPosition.maxHeight,
              }
            : { visibility: 'hidden' }}
          className={`fixed z-[10040] flex flex-col overflow-hidden rounded-2xl border border-white/12 bg-[#101217]/[0.985] shadow-[0_24px_64px_rgba(0,0,0,0.58),inset_0_1px_0_rgba(255,255,255,0.045)] backdrop-blur-2xl ${
            menuPosition?.placement === 'top' ? 'origin-bottom' : 'origin-top'
          }`}
        >
          <div className="h-px bg-gradient-to-r from-transparent via-cyan-300/45 to-transparent" />

          {/* Search input */}
          <div className="border-b border-white/[0.07] p-2">
            <div className="relative">
              <Icon
                icon="bx:search"
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/35"
                aria-hidden="true"
              />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="w-full rounded-xl border border-white/8 bg-white/[0.03] py-2 pl-8 pr-3 text-sm text-white placeholder:text-gray-600 focus:border-cyan-300/25 focus:outline-none focus:ring-1 focus:ring-cyan-300/20"
              />
            </div>
          </div>

          {/* Options list */}
          <div
            className="space-y-0.5 overflow-y-auto p-1.5"
            style={{ maxHeight: menuPosition ? Math.max(100, menuPosition.maxHeight - 112) : 250 }}
          >
            {filteredOptions.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-gray-500">
                No results{search ? ` for "${search}"` : ''}.
              </div>
            )}
            {filteredOptions.map((option, index) => {
              const selected = option.value === value;
              return (
                <button
                  ref={assignOptionRef(String(index))}
                  key={`${option.value}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => selectOption(option.value)}
                  onKeyDown={(event) => handleOptionKeyDown(event, String(index))}
                  className={`group flex w-full items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/25 ${
                    selected
                      ? 'border-cyan-300/20 bg-cyan-300/[0.08] text-white'
                      : 'border-transparent text-white/72 hover:border-white/[0.06] hover:bg-white/[0.045] hover:text-white'
                  }`}
                >
                  {option.emoji && (
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center text-base leading-none">
                      {option.emoji}
                    </span>
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{option.label}</span>
                  {option.hint && (
                    <span className="shrink-0 text-xs font-medium text-white/40">{option.hint}</span>
                  )}
                  <span
                    className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border transition-colors ${
                      selected
                        ? 'border-cyan-300/45 bg-cyan-300/15 text-cyan-100'
                        : 'border-white/12 text-transparent'
                    }`}
                  >
                    <Icon icon="bx:check" className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="w-full">
      {label ? (
        <span className={`block font-semibold text-gray-300 ${compact ? 'mb-1 text-[11px] font-bold uppercase tracking-[0.14em] text-white/45' : 'mb-2 text-sm'}`}>
          {label}{required && <span className="ml-0.5 text-cyan-300">*</span>}
        </span>
      ) : null}
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        // Native tooltip — long labels (CNAE activity names) truncate with an
        // ellipsis on the trigger; hovering reveals the full text.
        title={selectedOption?.label || undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={`group relative flex w-full items-center rounded-xl border bg-white/[0.04] text-left text-sm text-white transition-all hover:bg-white/[0.06] focus:outline-none focus:ring-2 ${
          compact ? 'h-10 px-3' : 'px-4 py-3.5'
        } ${
          borderless
            ? disabled
              ? 'cursor-not-allowed opacity-40'
              : 'border-transparent focus:ring-cyan-300/20'
            : disabled
              ? 'cursor-not-allowed border-white/5 opacity-50'
              : loading
                ? 'cursor-wait border-white/10'
                : 'border-white/10 focus:border-cyan-300/30 focus:ring-[#5260FF]'
        } ${triggerClassName}`}
      >
        {formatTrigger ? (
          <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-white/80">
            {formatTrigger(selectedOption) || placeholder}
          </span>
        ) : (
          <>
            {selectedOption?.emoji && (
              <span className="mr-2 text-base leading-none">{selectedOption.emoji}</span>
            )}
            <span className={`min-w-0 flex-1 truncate ${selectedOption ? 'font-medium' : 'text-gray-600'}`}>
              {selectedOption?.label || placeholder}
            </span>
            {selectedOption?.hint && (
              <span className="mr-1 shrink-0 text-xs font-medium text-white/45">{selectedOption.hint}</span>
            )}
          </>
        )}
        {loading ? (
          <svg className="h-4 w-4 shrink-0 animate-spin text-cyan-300/60" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <Icon
            icon="bx:chevron-down"
            className={`h-4 w-4 shrink-0 text-white/50 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        )}
      </button>
      {menu}
    </div>
  );
};
